import type { ComponentIssue, LinterResult } from '@digibee/linter';

import { isValidDoubleBracesAliasIdentifier } from '~/scenes/Build/helpers/doubleBracesAlias';

export const INVALID_DOUBLE_BRACES_ALIAS_ISSUE_NAME =
  'double-braces-alias-invalid-identifier';

type AliasComponent = {
  doubleBracesAlias?: string;
};

export type DraftDoubleBracesAlias = {
  componentId: string;
  alias: string;
} | null;

export const overlayDraftDoubleBracesAlias = <T extends AliasComponent>(
  configurations: Iterable<[string, T]>,
  draft: DraftDoubleBracesAlias
): Iterable<[string, T]> => {
  if (!draft) {
    return configurations;
  }

  return Array.from(configurations, ([id, config]) =>
    id === draft.componentId
      ? [id, { ...config, doubleBracesAlias: draft.alias }]
      : [id, config]
  );
};

const createInvalidAliasIssue = (alias: string): ComponentIssue => ({
  name: INVALID_DOUBLE_BRACES_ALIAS_ISSUE_NAME,
  type: 'component',
  severity: 'warning',
  category: 'best-practices',
  categoryLabel: 'label.linter_category_best_practices',
  message: 'label.double_braces_alias_invalid_identifier_error',
  messageAttributes: { alias }
});

export const removeInvalidAliasIssues = (
  linterResult: LinterResult
): LinterResult => {
  const components = Object.fromEntries(
    Object.entries(linterResult.components ?? {}).map(([trackName, byId]) => [
      trackName,
      Object.fromEntries(
        Object.entries(byId)
          .map(([id, issues]) => [
            id,
            issues.filter(
              issue => issue.name !== INVALID_DOUBLE_BRACES_ALIAS_ISSUE_NAME
            )
          ])
          .filter(([, issues]) => (issues as ComponentIssue[]).length > 0)
      )
    ])
  );

  return {
    ...linterResult,
    components
  };
};

export const mergeInvalidDoubleBracesAliasIssues = (
  linterResult: LinterResult,
  configurations: Iterable<[string, AliasComponent]>,
  getTrackName: (componentId: string) => string | undefined
): LinterResult => {
  let { components } = linterResult;
  let mutated = false;

  Array.from(configurations).forEach(([id, config]) => {
    const alias = config.doubleBracesAlias;
    if (!alias || isValidDoubleBracesAliasIdentifier(alias)) {
      return;
    }

    const trackName = getTrackName(id);
    if (!trackName) {
      return;
    }

    if (!mutated) {
      components = { ...linterResult.components };
      mutated = true;
    }

    const trackIssues = components[trackName] ?? {};
    const existing = trackIssues[id] ?? [];

    if (
      existing.some(
        issue => issue.name === INVALID_DOUBLE_BRACES_ALIAS_ISSUE_NAME
      )
    ) {
      return;
    }

    components[trackName] = {
      ...trackIssues,
      [id]: [...existing, createInvalidAliasIssue(alias)]
    };
  });

  if (!mutated) {
    return linterResult;
  }

  return {
    ...linterResult,
    components
  };
};

export const applyInvalidAliasIssuesToResult = (
  linterResult: LinterResult,
  configurations: Iterable<[string, AliasComponent]>,
  getTrackName: (componentId: string) => string | undefined
): LinterResult =>
  mergeInvalidDoubleBracesAliasIssues(
    removeInvalidAliasIssues(linterResult),
    configurations,
    getTrackName
  );
